// src/store/slices/chatSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

const API_URL = process.env.REACT_APP_CHAT_API_URL || 'https://chat-api.narodnichitalishta.bg';
const API_KEY = process.env.REACT_APP_CHAT_API_KEY || 'e773b5e37b8c0b4e6585411d40611bfae8116342833c647ab2550009ef24323f';

// Генериране на conversation_id
export const generateConversationId = () => {
  return `conv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Async thunks - SIMPLIFIED VERSION
export const sendChatMessage = createAsyncThunk(
  'chat/sendMessage',
  async ({ message, conversation_id = null, mode = 'medium' }, { rejectWithValue }) => {
    try {
      const currentConversationId = conversation_id || generateConversationId();
      
      const response = await fetch(`${API_URL}/chat/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY,
        },
        body: JSON.stringify({
          message,
          conversation_id: currentConversationId,
          mode,
          stream: false,
          output_format: 'text'
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      return {
        ...data,
        conversation_id: currentConversationId
      };
    } catch (error) {
      console.error('Грешка при изпращане на съобщение:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const sendChatMessageStream = createAsyncThunk(
  'chat/sendMessageStream',
  async ({ message, conversation_id = null, mode = 'medium' }, { dispatch, rejectWithValue }) => {
    try {
      const currentConversationId = conversation_id || generateConversationId();
      
      const response = await fetch(`${API_URL}/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY,
        },
        body: JSON.stringify({
          message,
          conversation_id: currentConversationId,
          mode,
          stream: true,
          output_format: 'text'
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullResponse = '';
      
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        const chunk = decoder.decode(value);
        fullResponse += chunk;
        dispatch(updateStreamingMessage(fullResponse));
      }

      return {
        answer: fullResponse,
        conversation_id: currentConversationId,
        intent: 'streaming',
        mode,
        metadata: {},
      };
    } catch (error) {
      console.error('Грешка при streaming съобщение:', error);
      return rejectWithValue(error.message);
    }
  }
);

const chatSlice = createSlice({
  name: 'chat',
  initialState: {
    messages: [
      {
        id: 1,
        text: "Здравейте! Аз съм вашият помощник за данни на читалищата. Как мога да ви помогна?",
        sender: "bot",
        timestamp: new Date().toISOString(),
      },
    ],
    loading: false,
    streaming: false,
    error: null,
    isOpen: false,
    currentConversationId: generateConversationId(),
    mode: 'medium',
    useStreaming: false,
    streamingMessage: null,
  },
  reducers: {
    addMessage: (state, action) => {
      state.messages.push({
        id: state.messages.length + 1,
        ...action.payload,
        timestamp: new Date().toISOString(),
      });
    },
    clearChat: (state) => {
      state.messages = [
        {
          id: 1,
          text: "Здравейте! Аз съм вашият помощник за данни на читалищата. Как мога да ви помогна?",
          sender: "bot",
          timestamp: new Date().toISOString(),
        },
      ];
      state.currentConversationId = generateConversationId();
      state.error = null;
      state.streamingMessage = null;
    },
    toggleChat: (state) => {
      state.isOpen = !state.isOpen;
    },
    openChat: (state) => {
      state.isOpen = true;
    },
    closeChat: (state) => {
      state.isOpen = false;
    },
    setMode: (state, action) => {
      state.mode = action.payload;
    },
    setUseStreaming: (state, action) => {
      state.useStreaming = action.payload;
    },
    updateStreamingMessage: (state, action) => {
      state.streamingMessage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // sendChatMessage
      .addCase(sendChatMessage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendChatMessage.fulfilled, (state, action) => {
        state.loading = false;
        state.currentConversationId = action.payload.conversation_id;
        
        // Проверка за валиден отговор
        if (action.payload.answer) {
          state.messages.push({
            id: state.messages.length + 1,
            text: action.payload.answer,
            sender: 'bot',
            timestamp: new Date().toISOString(),
          });
        } else {
          // Ако няма отговор, показваме грешка
          state.messages.push({
            id: state.messages.length + 1,
            text: "Сървърът не върна отговор. Моля, опитайте отново.",
            sender: 'bot',
            timestamp: new Date().toISOString(),
          });
        }
      })
      .addCase(sendChatMessage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.messages.push({
          id: state.messages.length + 1,
          text: `Грешка: ${action.payload || "Моля, опитайте отново."}`,
          sender: 'bot',
          timestamp: new Date().toISOString(),
        });
      })
      
      // sendChatMessageStream
      .addCase(sendChatMessageStream.pending, (state) => {
        state.loading = true;
        state.streaming = true;
        state.error = null;
        state.streamingMessage = '';
      })
      .addCase(sendChatMessageStream.fulfilled, (state, action) => {
        state.loading = false;
        state.streaming = false;
        state.currentConversationId = action.payload.conversation_id;
        state.streamingMessage = null;
        
        if (action.payload.answer) {
          state.messages.push({
            id: state.messages.length + 1,
            text: action.payload.answer,
            sender: 'bot',
            timestamp: new Date().toISOString(),
            intent: 'streaming',
          });
        }
      })
      .addCase(sendChatMessageStream.rejected, (state, action) => {
        state.loading = false;
        state.streaming = false;
        state.error = action.payload;
        state.streamingMessage = null;
        state.messages.push({
          id: state.messages.length + 1,
          text: `Streaming грешка: ${action.payload || "Моля, опитайте отново."}`,
          sender: 'bot',
          timestamp: new Date().toISOString(),
        });
      });
  },
});

export const { 
  addMessage, 
  clearChat, 
  toggleChat, 
  openChat, 
  closeChat,
  setMode,
  setUseStreaming,
  updateStreamingMessage,
} = chatSlice.actions;

export default chatSlice.reducer;